import { Outlet, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { AppHeader } from '@/components/layout'
import { Skeleton } from '@/components/ui'
import { api, type AppFullInfo } from '@/api/client'

export interface AppStats {
  scope_count: number
  role_count: number
  webhook_count: number
  authorization_count: number
}

export function AppDetailLayout() {
  const { orgSlug, appId } = useParams()
  const [app, setApp] = useState<AppFullInfo | null>(null)
  const [stats, setStats] = useState<AppStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchApp = async () => {
      if (!orgSlug || !appId) return
      try {
        const response = await api.apps.get(orgSlug, appId)
        setApp(response.app)
        setStats({
          scope_count: response.scope_count,
          role_count: response.role_count,
          webhook_count: response.webhook_count,
          authorization_count: response.authorization_count,
        })
      } catch (err) {
        console.error('Failed to fetch app:', err)
      } finally {
        setIsLoading(false)
      }
    }
    fetchApp()
  }, [orgSlug, appId])

  if (isLoading) {
    return (
      <div>
        <div className="border-b border-[var(--border-default)] bg-[var(--bg-elevated)] px-6 py-4">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="p-6">
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  if (!app) {
    return (
      <div className="p-6">
        <p className="text-[var(--text-secondary)]">App not found</p>
      </div>
    )
  }

  return (
    <div>
      <AppHeader
        appName={app.name}
        appSlug={app.slug}
        status={app.status as 'active' | 'inactive' | 'draft'}
      />
      <Outlet context={{ app, setApp, stats }} />
    </div>
  )
}
